import React, { useMemo } from 'react';
import { Tooltip } from 'antd';

import DataTable from '../../../../../../../../shared/designSystem/components/DataTable';
import {
  formatCurrency2SigFig,
  formatWholeNumber,
} from '../../../../../../../../../core/utils/campaigns';

// =============================================================================
// ReferrerTable — V1 "Basket Items" table on the shared DataTable primitive.
// Columns: color swatch / Product Name / Revenue / Quantity. Data shape
// preserved (UPPERCASE NAME / REVENUE / QUANTITY + color assigned by the
// parent layout). Parent feeds rows revenue-desc — top sellers surface
// first, matching V2's BasketItemDetails feed order so the table row order
// lines up with the sibling ring chart's slice order. Alphabetical view is
// one click away via DataTable's sortable Product Name column.
//
// Mobile + totals parity with V2's BasketItemTable: card-mode below md,
// summary row with Revenue + Quantity sums.
// =============================================================================

const renderProductName = (text) => {
  const t = text || '—';
  if (String(t).length > 45) {
    return (
      <Tooltip overlayClassName="mjl-ds" title={t}>
        <span className="campaign-overview__creatives-name">
          {String(t).slice(0, 46)}…
        </span>
      </Tooltip>
    );
  }
  return <span className="campaign-overview__creatives-name">{t}</span>;
};

const ReferrerTable = ({ referrerData = [] }) => {
  const rows = useMemo(
    () =>
      referrerData.map((item, i) => ({
        ...item,
        key: i,
      })),
    [referrerData]
  );

  const columns = [
    {
      id: 'color',
      header: '',
      accessorKey: 'color',
      sortable: false,
      meta: { align: 'center', width: 32 },
      cell: ({ getValue }) => (
        <span
          className="campaign-overview__creatives-dot"
          style={{ backgroundColor: getValue() || '#cbd5e1' }}
          aria-hidden="true"
        />
      ),
    },
    {
      id: 'NAME',
      accessorKey: 'NAME',
      header: 'Product Name',
      sortable: true,
      sortingFn: (a, b) =>
        String(a.original.NAME || '').localeCompare(String(b.original.NAME || '')),
      meta: { align: 'left' },
      cell: ({ getValue }) => renderProductName(getValue()),
    },
    {
      id: 'REVENUE',
      accessorKey: 'REVENUE',
      header: 'Revenue',
      sortable: true,
      sortingFn: (a, b) =>
        (Number(a.original.REVENUE) || 0) - (Number(b.original.REVENUE) || 0),
      meta: { align: 'right', width: 110 },
      cell: ({ getValue }) => (
        <span>{formatCurrency2SigFig(Number(getValue()) || 0)}</span>
      ),
    },
    {
      id: 'QUANTITY',
      accessorKey: 'QUANTITY',
      header: 'Quantity',
      sortable: true,
      sortingFn: (a, b) =>
        (Number(a.original.QUANTITY) || 0) - (Number(b.original.QUANTITY) || 0),
      meta: { align: 'right', width: 100 },
      cell: ({ getValue }) => (
        <span>{formatWholeNumber(Number(getValue()) || 0)}</span>
      ),
    },
  ];

  // Totals — sum across the (already-filtered + revenue-sorted) rows.
  // Matches V2's BasketItemTable summaryRow shape.
  const totalRevenue = useMemo(
    () => rows.reduce((sum, d) => sum + (parseFloat(d.REVENUE) || 0), 0),
    [rows]
  );
  const totalQuantity = useMemo(
    () => rows.reduce((sum, d) => sum + (parseFloat(d.QUANTITY) || 0), 0),
    [rows]
  );

  // Mobile card — color dot + product name on header, two metric tiles
  // (Revenue + Quantity). Mirrors V2's BasketItemTable card shape so both
  // versions read identically on phones.
  const renderMobileCard = (record) => (
    <>
      <div className="campaign-overview__creatives-card-head">
        <div className="campaign-overview__creatives-card-name">
          <span
            className="campaign-overview__creatives-dot"
            style={{ backgroundColor: record.color || '#cbd5e1' }}
            aria-hidden="true"
          />
          {renderProductName(record.NAME)}
        </div>
      </div>
      <div className="campaign-overview__creatives-card-metrics">
        <div className="campaign-overview__creatives-card-metric">
          <span className="campaign-overview__creatives-card-metric-label">Revenue</span>
          <span className="campaign-overview__creatives-card-metric-value">
            {formatCurrency2SigFig(Number(record.REVENUE) || 0)}
          </span>
        </div>
        <div className="campaign-overview__creatives-card-metric">
          <span className="campaign-overview__creatives-card-metric-label">Quantity</span>
          <span className="campaign-overview__creatives-card-metric-value">
            {formatWholeNumber(Number(record.QUANTITY) || 0)}
          </span>
        </div>
      </div>
    </>
  );

  return (
    <DataTable
      data={rows}
      columns={columns}
      pageSize={10}
      pageSizeOptions={[10, 20, 50, 100]}
      mobileBreakpoint="md"
      renderMobileCard={renderMobileCard}
      summaryRow={{
        labelColumnId: 'NAME',
        label: 'Totals',
        cells: {
          REVENUE: formatCurrency2SigFig(totalRevenue),
          QUANTITY: formatWholeNumber(totalQuantity),
        },
      }}
      emptyState="No basket items yet."
    />
  );
};

export default ReferrerTable;
